import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, filter, skip } from 'rxjs/operators';

import once from 'one-event';
import { once as _once } from 'underscore';

import { IDualPlaybackLayoutOption } from '@vbrick/vbrick-player-conference-dual-hls-plugin/IDualPlaybackLayoutOption';

import { IVbSource } from 'vbrick-player-src/IVbSource';
import { IVbTextTrack } from 'vbrick-player-src/TextTrackHelper.Service';
import { ISubtitleOption } from '../ISubtitleOption';
import { PlaybackOption } from 'vbrick-player-src/PlaybackOption';
import { userAgentUtil } from 'vbrick-player-src/UserAgentUtil';
import { IVbrickPlayerConfig } from 'vbrick-player-src/IVbrickPlayerConfig';

import { VG_VOLUME_KEY } from './VgConstants';
import { VgFullscreenService } from './VgFullscreen.Service';
import { VgStates } from './VgStates';
import { VgUtilsService } from './VgUtils.Service';

import styles from './Videogular.module.less';
import { NotAllowedError } from '../MediaErrorCode';

type PlaybackPluginLoader = (source: IVbSource) => false | (() => void);
export type SubtitleType = ISubtitleOption | TextTrack | string;

interface ISeekEvent {
	$currentTime: number;
	$duration: number;
}

const NOOP = () => { /* noop */ };
const SKIP_TIME_MS = 10000;

/**
 * Main component that must wrap a &lt;vg-media&gt; tag and all plugins.
 *
 * &lt;video&gt; tag usually will be above plugin tags, that's because plugins should be in a layer over the &lt;video&gt;.
 *
 * @param {string} vgTheme String with a scope name variable. This directive will inject a CSS link in the header of your page.
 * **This parameter is required.**
 *
 * @param {boolean} [vgPlaysInline=false] vgPlaysInline Boolean value or a String with a scope name variable to use native fullscreen (default) or set fullscreen inside browser (true).
 *
 * @param {boolean} [vgAutoPlay=false] vgAutoPlay Boolean value or a String with a scope name variable to auto start playing video when it is initialized.
 *
 * **This parameter is disabled in mobile devices** because user must click on content to prevent consuming mobile data plans.
 *
 * @param {boolean} [vgMutedAutoPlay=false] vgMutedAutoPlay Boolean value to apply muted autoplay behavior.
 *
 * @param {boolean} [vgStartTime=-1] vgStartTime Number value or a String with a scope name variable to start playing the video at a certain time.
 *
 * @param {function} vgComplete Function name in controller's scope to call when video have been completed.
 * @param {function} vgUpdateState Function name in controller's scope to call when video state changes. Receives a param with the new state. Possible values are "play", "stop" or "pause".
 * @param {function} vgPlayerReady Function name in controller's scope to call when video have been initialized. Receives a param with the videogular API.
 * @param {function} vgSeeking Function name in controller's scope to call when the video has finished jumping to a new time. Receives a param with the seeked time and duration in seconds.
 * @param {function} vgSeeked Function name in controller's scope to call when the video is jumping to a new time. Receives two params with the seeked time and duration in seconds.
 * @param {function} vgError Function name in controller's scope to receive an error from video object. Receives a param with the error event.
 * @param {function} vgActiveSourceChange Function name in the controller's scope to call when the active source has changed. Receives as a param the new activeSource object.
 * This is a free parameter and it could be values like "new.mp4", "320" or "sd". This will allow you to use this to change a video or video quality.
 * This callback will not change the video, you should do that by updating your sources scope variable.
 *
 * This controller offers a public API:
 *
 * Methods
 * - play(): Plays media.
 * - pause(): Pause media.
 * - stop(): Stops media.
 * - playPause(): Toggles play and pause.
 * - seekTime(value, byPercent): Seeks to a specified time position. Param value must be an integer representing the target position in seconds or a percentage. By default seekTime seeks by seconds, if you want to seek by percentage just pass byPercent to true.
 * - setVolume(volume): Sets volume. Param volume must be an integer with a value between 0 and 1.
 * - setPlayback(playback): Sets playback speed. Param plaback must be an integer with a value between 0 and 2.
 * - setState(state): Sets a new state. Param state mus be an string with 'play', 'pause' or 'stop'. This method only changes the state of the player, but doesn't plays, pauses or stops the media file.
 * - toggleFullScreen(): Toggles between fullscreen and normal mode.
 * - clearMedia(): Cleans the current media file.
 * - changeSource(array): Updates current media source. Param `array` must be an array of media source objects.
 * - changeActiveSource(sourceObj): Updates the value tracked for the member of sources that is being used for playback.
 * A media source is an object with two properties `src` and `type`. The `src` property must contains a trustful url resource.
 * <pre>{src: $sce.trustAsResourceUrl("http://static.videogular.com/assets/videos/videogular.mp4"), type: "video/mp4"}</pre>
 *
 * Properties
 * - config: String with a url to JSON config file.
 * - isReady: Boolean value with current player initialization state.
 * - isBuffering: Boolean value to know if player is buffering media.
 * - isCompleted: Boolean value to know if current media file has been completed.
 * - isLive: Boolean value to know if current media file is a Live Streaming.
 * - playsInline: Boolean value to know if Videogular is using inline playing or not.
 * - nativeFullscreen: Boolean value to know if Videogular if fullscreen mode will use native mode or emulated mode.
 * - mediaElement: Reference to video/audio object.
 * - videogularElement: Reference to videogular tag.
 * - sources: Array with current sources.
 * - activeSource: Object memeber of the sources array that is presently the active playback.
 * - isFullScreen: Boolean value to know if we’re in fullscreen mode.
 * - currentState: String value with “play”, “pause” or “stop”.
 * - currentTime: Number value with current media time progress.
 * - totalTime: Number value with total media time.
 * - timeLeft: Number value with current media time left.
 * - volume: Number value with current volume between 0 and 1.
 * - playback: Number value with current playback between 0 and 2.
 * - bufferEnd: Number value with latest buffer point in milliseconds.
 * - buffered: Array of TimeRanges objects that represents current buffer state.
 */
@Component({
	selector: 'videogular',
	host: {
		'[class]': 'styles.root'
	},
	template: `<ng-content></ng-content>`
})
export class VideogularComponent implements OnDestroy, OnChanges, OnInit {
	@Input() public vgAutoPlay: boolean;
	@Input() public vgMutedAutoPlay: boolean;
	@Input() public forceMute: boolean; //Disables audio playback
	@Input() public vgStartTime: number; // seconds
	@Input() public forceEndTimeInSec: number;

	@Output() public vgActiveSourceChange: EventEmitter<{ $source: IVbSource }> = new EventEmitter();
	@Output() public vgActiveSubtitleChange: EventEmitter<{ subtitle: ISubtitleOption }> = new EventEmitter();
	@Output() public vgComplete: EventEmitter<void> = new EventEmitter();
	@Output() public vgError: EventEmitter<{ $event: any }> = new EventEmitter();
	@Output() public vgPlayerReady: EventEmitter<{ $API: VideogularComponent }> = new EventEmitter();
	@Output() public vgResetStartTime: EventEmitter<number> = new EventEmitter();
	@Output() public vgSeeked: EventEmitter<ISeekEvent> = new EventEmitter();
	@Output() public vgSeeking: EventEmitter<ISeekEvent> = new EventEmitter();
	@Output() public vgUpdateState: EventEmitter<{ $state: VgStates }> = new EventEmitter();
	@Output() public vgVolumeChanged: EventEmitter<{ volume: number }> = new EventEmitter();
	@Output() public vgPlaybackRateChanged: EventEmitter<{ playbackRate: number }> = new EventEmitter();

	public readonly styles = styles;
	public autoLoad: boolean;
	public activeSource: PlaybackOption;
	public analyticsEnabled: boolean = true;
	public autoPlay: boolean;
	public bandwidth: number;
	public bitrate: number;
	public bufferEnd: number;
	public buffered: TimeRanges & { realEnd?: () => number }; // realEnd used by flash plugin
	public config: IVbrickPlayerConfig;
	public currentState: VgStates;
	public currentSubtitle: string;
	public defaultVolume: number = 1;
	public dualPlaybackLayout: IDualPlaybackLayoutOption;
	public forceFlashHlsPlayback: boolean;
	public ignoreDomErrors: boolean;
	public isBuffering: boolean;
	public isBufferingOverride: boolean;
	public isCaptionsAvailable: boolean;
	public isCompleted: boolean;
	public isDualPlayback: boolean;
	public isDualPlaybackActive: boolean;
	public isForceHtmlSubtitleRendering: boolean;
	public isHtmlSubtitleRenderingEnabled: boolean;
	public isIgnoreDecodingErrors: boolean;
	public isLive: boolean;
	public isPlaybackPluginLoading: boolean;
	public isReady: boolean;
	public isSupportedPlaybackOptions: boolean;
	public mediaElement: HTMLVideoElement;
	/** Playback speed */
	public playback: number;
	public playbackPluginUnload: () => void;
	public setCaptionsEnabled: (isEnabled: boolean) => void;
	public setSubtitleTrackInPlugin: (show: boolean, subtitle: TextTrack) => void;
	public sources: IVbSource[];
	public startTime: number; // seconds
	public timeLeft: number; // milliseconds
	public totalTime: number; // milliseconds
	public volume: number;
	public currentTime$: Observable<number>;
	public startTimeFetchFunc = () => {
		return this.isResetStartTime ? 0 : this.startTime;
	};
	public flashDisabled: boolean;

	private endFullScreenTime: Date;
	private hasStartTimePlayed: boolean = false;
	public isFullScreen: boolean;
	private isFullScreenPressed: boolean = false;
	private isLocalStorageSupported: boolean;
	private isMetaDataLoaded: boolean = false;
	private isMutedAutoPlayCompleted: boolean;
	private lastSeekTime: number;
	private pauseTime: Date;
	private playbackPluginsLoaders: PlaybackPluginLoader[] = [];
	private seekStartTime: number;

	private currentTimeSubject$: BehaviorSubject<number>;
	private textTracksSubject$: BehaviorSubject<IVbTextTrack[]> = new BehaviorSubject<IVbTextTrack[]>([]);
	public activeSubtitle: SubtitleType;

	constructor(
		private elementRef: ElementRef,
		private VG_UTILS: VgUtilsService,
		private vgFullscreen: VgFullscreenService,
		private zone: NgZone
	) {}

	public ngOnInit(): void {
		this.bufferEnd = 0;
		this.buffered = this.getEmptyTimeRanges();
		this.currentState = this.autoPlay ? VgStates.PLAY : VgStates.STOP;
		this.isCompleted = false;
		this.isFullScreen = false;
		this.isLocalStorageSupported = this.VG_UTILS.supportsLocalStorage();
		this.isLive = false;
		this.isMutedAutoPlayCompleted = false;
		this.isReady = false;
		this.playback = 1;
		this.timeLeft = 0;
		this.totalTime = 0;
		this.volume = 1;

		this.currentTimeSubject$ = new BehaviorSubject<number>(0);
		this.currentTime$ = this.currentTimeSubject$.asObservable();

		if (this.vgFullscreen.isAvailable) {
			this.isFullScreen = this.vgFullscreen.isFullScreen();
			document.addEventListener(this.vgFullscreen.onchange, this.onFullScreenChange);
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (!this.isReady) {
			return;
		}

		if (changes.vgAutoPlay) {
			this.onUpdateAutoPlay(this.vgAutoPlay);
		}

		if (changes.vgStartTime) {
			this.onUpdateStartTime(this.vgStartTime);
		}
	}

	public ngOnDestroy(): void {
		document.removeEventListener(this.vgFullscreen.onchange, this.onFullScreenChange);

		// Empty mediaElement on destroy to avoid that Chrome downloads video even when it's not present
		this.clearMedia();
	}

	public get currentTime(): number {
		return this.currentTimeSubject$?.value; // milliseconds
	}

	// This is needed for legacy player.
	// currentTime getting set outside as well
	public set currentTime(value: number) {
		this.currentTimeSubject$.next(value);
	}

	public get playbackStarted$() {
		return this.currentTimeSubject$.pipe(
			filter(val => !!val && this.currentState === VgStates.PLAY),
			skip(2),
			first()
		);
	}

	public onUpdateTrack(): void {
		this.textTracksSubject$.next(Array.from(this.rawMediaElement.textTracks));
	}

	public get textTracks$(): Observable<IVbTextTrack[]> {
		return this.textTracksSubject$.asObservable();
	}

	public get isMuted(): boolean {
		return this.volume === 0;
	}

	public get numPlaybackPlugins(): number {
		return this.playbackPluginsLoaders.length;
	}

	public get rawMediaElement(): HTMLMediaElement {
		return this.mediaElement; // TODO: remove
	}

	public get videogularElement(): HTMLElement {
		return this.elementRef.nativeElement;
	}

	public attemptPlaybackThroughPlugin(source: IVbSource): false | (() => void) {
		for (const playbackPluginLoader of this.playbackPluginsLoaders) {
			const loaderResult = playbackPluginLoader(source);

			if (loaderResult) {
				return loaderResult;
			}
		}

		return false;
	}

	public changeActiveSource(activeSource: PlaybackOption): void {
		this.activeSource = activeSource;
		this.isLive = activeSource && !!activeSource.isLive;

		this.vgActiveSourceChange.emit({ $source: activeSource });
	}

	public clearMedia(): void {
		if (this.playbackPluginUnload) {
			this.playbackPluginUnload();
			this.playbackPluginUnload = null;
		}
	}

	public enterElementInFullScreen(element: HTMLElement): void {
		this.vgFullscreen.request(element);
	}

	public isMutedAutoPlayCompletionAllowed(): boolean {
		const userAgent: string = navigator.userAgent;
		const isSafari: boolean = userAgent.includes('Safari') && !userAgent.includes('Chrome');

		return !isSafari ||
			!this.isShimmedMediaElement(); // Safari is very strict about muted autoplay. It doesn't factor in domain interactions. Only volume changes on the video element, so ignore changes from shimmed.
	}

	public isShimmedMediaElement(): boolean {
		return this.rawMediaElement.tagName !== 'VIDEO';
	}

	public onCanPlay(): void {
		this.isBuffering = false;

		if (!this.hasStartTimePlayed && this.startTime > 0) {
			if(this.isResetStartTime) {
				this.vgResetStartTime.emit(0);
			} else {
				this.seekTime(this.startTime);
			}
			this.hasStartTimePlayed = true;
		}
	}

	public get isResetStartTime(): boolean {
		return this.startTime > this.rawMediaElement?.duration;
	}

	public onComplete(): void {
		this.vgComplete.emit();

		this.setState(VgStates.STOP);
		this.isCompleted = true;
	}

	public onFullScreenChange = (): void => {
		this.isFullScreen = this.vgFullscreen.isFullScreen();
	};

	public oniOSEndFullScreen(): void {
		const pauseDiff = +new Date() - +this.pauseTime;

		if (pauseDiff < 1000) { // if difference between full screen end and pause is < 1 sec, assume it was not triggered by the user
			this.playInternal();
		} else {
			this.endFullScreenTime = new Date();
		}
	}

	public onLoadMetaData(evt: Event): void {
		this.isBuffering = false;
		this.onUpdateTime(evt);
	}

	public onPause(): void {
		this.pauseTime = new Date();
		const currentTime: number = this.rawMediaElement.currentTime;

		this.setState(currentTime === 0 ?
			VgStates.STOP :
			VgStates.PAUSE
		);

		const endFsDiff = +new Date() - +this.endFullScreenTime;

		if (endFsDiff < 1000) { // if difference between full screen end and pause is < 1 sec, assume it was not triggered by the user
			this.playInternal();
		}
	}

	public onPlay(): void {
		this.setState(VgStates.PLAY);
	}

	public onPlaybackChange(): void {
		this.playback = this.rawMediaElement.playbackRate;
	}

	public onProgress(event: ProgressEvent): void {
		this.updateBuffer(event);
	}

	public onSeeked(event: Event): void {
		const target: HTMLMediaElement = event.target as HTMLMediaElement;

		this.vgSeeked.emit({
			$currentTime: target.currentTime,
			$duration: target.duration
		});
	}

	public onSeeking(event: Event): void {
		const target: HTMLMediaElement = event.target as HTMLMediaElement;

		this.vgSeeking.emit({
			$currentTime: typeof this.seekStartTime === 'number' ?
				this.seekStartTime :
				target.currentTime,
			$duration: target.duration
		});
		this.seekStartTime = null;
	}

	public onStartBuffering(): void {
		this.isBuffering = true;

		this.triggerChangeDetection();
	}

	public onStartPlaying(): void {
		this.isBuffering = false;

		this.triggerChangeDetection();
	}

	public onUpdateAutoPlay(newValue: boolean): void {
		const isChangeToAutoPlay: boolean = newValue && !this.autoPlay;

		this.autoPlay = newValue;

		if (isChangeToAutoPlay) {
			this.play();
		}
	}

	public onUpdateStartTime(newValue: number, applyLater?: boolean): void {
		if (newValue && (newValue !== this.startTime)) {
			if (applyLater) {
				this.startTime = newValue;
				this.hasStartTimePlayed = false; //vbrick fix to actually reset the flag
			} else {
				this.rawMediaElement.currentTime = newValue;
				this.startTime = newValue;

				const fakeEvent = {
					target: this.rawMediaElement
				} as any;
				this.onUpdateTime(fakeEvent);
			}
		}
	}

	public onUpdateTime(event: Event): void {
		const target: HTMLMediaElement = event.target as HTMLMediaElement;
		const targetTime: number = 1000 * target.currentTime;
		const isLiveSourceOverride: boolean = this.activeSource && !!this.activeSource.isLive;

		if (!!this.forceEndTimeInSec && targetTime >= this.forceEndTimeInSec * 1000) {
			this.onComplete();
			return;
		}

		this.updateBuffer(event);

		if (target.duration !== Infinity) {
			if (!targetTime && this.lastSeekTime > 0) { //bogus time update value thrown while seeking, so short-circuit
				return;
			}

			this.totalTime = 1000 * target.duration;
			this.timeLeft = 1000 * (target.duration - target.currentTime);
		}

		//when the targetTime surpasses the last seek time, discard the last seek time
		if (targetTime > this.lastSeekTime) {
			this.lastSeekTime = null;
		}

		this.isLive = isLiveSourceOverride;
		this.currentTimeSubject$.next(targetTime);

		this.triggerChangeDetection();
	}

	public onVideoError(event?: Event): void {
		//electron app (esp - microsoft teams) on android mobile throwing error. (root cause not found)
		//but playback working well if error is ignored.
		if (this.config && this.config.isElectronMobileApp &&
				event && event.currentTarget === this.rawMediaElement && event.type === 'error' &&
				this.rawMediaElement && !this.rawMediaElement.error) {
			return;
		}

		this.vgError.emit({ $event: event });
	}

	public onVideoReady(): void {
		this.isReady = true;
		this.onUpdateAutoPlay(this.vgAutoPlay);
		this.startTime = this.vgStartTime;
		this.currentState = this.autoPlay ? VgStates.PLAY : VgStates.STOP;
		this.isMetaDataLoaded = true;

		//Set media volume from localStorage if available
		if (!this.vgMutedAutoPlay && this.isLocalStorageSupported) {
			//Default to 100% volume if local storage setting does not exist.
			this.setVolume(parseFloat(localStorage.getItem(VG_VOLUME_KEY) || '1'));
		}

		if (this.vgAutoPlay) {
			this.onUpdateAutoPlay(this.vgAutoPlay);
		}

		if (this.vgStartTime) {
			this.onUpdateStartTime(this.vgStartTime);
		}

		this.vgPlayerReady.emit({ $API: this });

		this.triggerChangeDetection();
	}

	public onVolumeChange(): void {
		const newVolume: number = this.rawMediaElement.volume;

		if (typeof newVolume !== 'number') {
			return;
		}

		this.volume = newVolume;

		this.rawMediaElement.muted = !this.volume;

		if (this.vgMutedAutoPlay && !this.isMutedAutoPlayCompleted && this.autoPlay && this.volume && this.isMutedAutoPlayCompletionAllowed()) {
			this.isMutedAutoPlayCompleted = true;
		}

		this.vgVolumeChanged.emit({ volume: newVolume });
	}

	public registerPlaybackPlugin(pluginLoader: PlaybackPluginLoader): void {//pluginLoader is a function which should accept src, type
		if (pluginLoader instanceof Function) {
			this.playbackPluginsLoaders.push(pluginLoader);
		} else {
			throw new TypeError('Expecting a Function which should accept src, type.');
		}
	}

	public pause(): void {
		try {
			this.rawMediaElement.pause();
		} catch(e) {
			// deliberately empty
		}

		this.setState(VgStates.PAUSE);
	}

	public play(): void {
		//if live AND native video element, apply resume behavior to start playback at the live edge (HLS presently only impacted type)
		if (this.isLive && !this.isShimmedMediaElement()) {
			if (Number.isFinite(this.rawMediaElement.duration)) { //duration is populated
				this.resume();
			} else { //metadata not yet available
				once(this.mediaElement, 'durationchange', () => this.resume());
			}
		}

		this.playInternal();
	}

	public playPause(): void {
		this.rawMediaElement.paused ?
			this.play() :
			this.pause();
	}

	public resetVolumeIfNeeded(): void {
		const muteAutoplay = this.vgAutoPlay && this.vgMutedAutoPlay && !this.isMutedAutoPlayCompleted;

		if(muteAutoplay || this.forceMute) {
			if (this.volume === 0) {
				this.onVolumeChange();
			} else {
				this.setVolume(0);
			}
		}
	}

	public setPlayback(playbackRate: number): void {
		this.rawMediaElement.playbackRate = playbackRate;
		this.playback = playbackRate;
		this.vgPlaybackRateChanged.emit({ playbackRate });
	}

	public setActiveSubtitle(subtitle: SubtitleType): void {
		this.activeSubtitle = subtitle;
		this.vgActiveSubtitleChange.emit({ subtitle: subtitle as any });
	}

	public seekTime(value: number): void {
		if (!Number.isFinite(value)) {
			return;
		}

		const second: number = value;
		this.seekStartTime = this.currentTime / 1000; //coverting ms to sec...

		this.rawMediaElement.currentTime = second;

		this.lastSeekTime = 1000 * second;

		this.currentTimeSubject$.next(this.lastSeekTime);

		// workaround timeupdate events not natively being dispatched when metadata only is applied
		if (this.rawMediaElement.buffered && !this.rawMediaElement.buffered.length) {
			const timeUpdateEvent: Event = document.createEvent('Event');

			timeUpdateEvent.initEvent('timeupdate', true, true);
			this.rawMediaElement.dispatchEvent(timeUpdateEvent);
		}
	}

	public setState(newState: VgStates): VgStates {
		// VBrick custom: prevent the state from making an undesirable transition from STOP to PAUSE.
		const isPauseWhileStopped: boolean =
			this.activeSource &&
			this.activeSource.isLive &&
			newState === VgStates.PAUSE &&
			this.currentState === VgStates.STOP;

		if (newState && newState !== this.currentState && !isPauseWhileStopped) {
			this.vgUpdateState.emit({ $state: newState });

			this.currentState = newState;
		}

		return this.currentState;
	}

	public setVolume(newVolume: number): void {
		if (typeof newVolume !== 'number' || !isFinite(newVolume)) {
			return;
		}

		if(this.forceMute) {
			newVolume = 0;
		}

		newVolume = Math.max(Math.min(newVolume, 1), 0);

		this.rawMediaElement.volume = newVolume;
		this.volume = newVolume;

		// event is not fired reliably, so manually trigger the handler
		this.onVolumeChange();

		//Push volume updates to localStorage so that future instances resume volume, but don't bother storing when doing muted auto play
		if (!this.vgMutedAutoPlay && this.isLocalStorageSupported) {
			//TODO: Improvement: concat key with current page or "video player id" to create separate stored volumes.
			localStorage.setItem(VG_VOLUME_KEY, newVolume.toString());
		}

		this.triggerChangeDetection();
	}

	public stop(): void {
		try {
			this.rawMediaElement.pause();

			this.buffered = this.getEmptyTimeRanges();
			this.bufferEnd = 0;
			this.setState(VgStates.STOP);
		}
		catch (e) {
			return e;
		}
	}

	public toggleFullScreen(): void {
		// There is no native full screen support or we want to play inline
		if (!this.vgFullscreen.isAvailable) {
			if (this.isFullScreen) {
				this.videogularElement.classList.remove(styles.fullscreen);
				this.videogularElement.style.zIndex = 'auto';
			} else {
				this.videogularElement.classList.add(styles.fullscreen);
				this.videogularElement.style.zIndex = '' + this.VG_UTILS.getZIndex();
			}

			this.isFullScreen = !this.isFullScreen;
		} else { // Perform native full screen support
			if (this.isFullScreen) {
				if (!userAgentUtil.isMobile) {
					this.vgFullscreen.exit();
				}
			} else {
				// On mobile devices we should make fullscreen only the video object
				if (userAgentUtil.isMobile) {
					// On iOS we should check if user pressed before fullscreen button
					// and also if metadata is loaded
					if (userAgentUtil.isSafariIos) {
						if (this.isMetaDataLoaded) {
							this.enterElementInFullScreen(this.rawMediaElement);
						} else {
							this.isFullScreenPressed = true;
							this.play();
						}
					} else {
						this.enterElementInFullScreen(this.rawMediaElement);
					}
				} else {
					this.enterElementInFullScreen(this.videogularElement);
				}
			}
		}
	}

	public toggleSound(): void {
		this.setVolume(
			this.isMuted ?
				this.defaultVolume :
				0
		);
	}

	public updateBuffer(event: Event): void {
		const target: HTMLMediaElement = event.target as HTMLMediaElement;

		if (target.buffered.length) {
			this.buffered = target.buffered;
			this.bufferEnd = 1000 * target.buffered.end(target.buffered.length - 1);

			// Avoid bufferEnd overflow by virtual clips
			if (this.bufferEnd > this.totalTime) {
				this.bufferEnd = this.totalTime;
			}
		}
	}

	public seek10Secs(forward: boolean): void {
		const currentTimeMs = this.currentTime;
		const timeMs = forward ? this.getForwardSkipTime(currentTimeMs) : this.getBackwardSkipTime(currentTimeMs);
		this.seekTime(timeMs / 1000);
	}

	private getForwardSkipTime(currentTimeMs: number): number {
		return Math.min(this.totalTime, currentTimeMs + SKIP_TIME_MS);
	}

	private getBackwardSkipTime(currentTimeMs: number): number {
		return currentTimeMs < SKIP_TIME_MS
			? 0 : currentTimeMs - SKIP_TIME_MS;
	}

	private getEmptyTimeRanges(): TimeRanges {
		function emptyRangeGet(): number {
			return -1;
		}

		return {
			start: emptyRangeGet,
			end: emptyRangeGet,
			length: 0
		};
	}

	private playInternal(): void {
		try {
			const playPromise = this.rawMediaElement.play();

			if (playPromise && playPromise.catch) {
				playPromise.catch(err => {
					if (err.name === NotAllowedError) {
						console.warn('from videogular play internal', err, err.name);
						this.handleUnmutedAutoplayError();
					}
				});
			}
		} catch (e) {
			// deliberately empty
		}

		this.setState(VgStates.PLAY);
	}

	private resume(): void {
		const mediaElement: HTMLMediaElement = this.rawMediaElement;
		const vSeekable: TimeRanges = mediaElement.seekable;
		const lastEndIndex: number = vSeekable.end.length - 1;

		//seek to the live edge
		if (vSeekable.length) {
			const vSeekableEnd = lastEndIndex >= 0 ?
				vSeekable.end(lastEndIndex) :
				mediaElement.duration;

			if (
				!Number.isFinite(vSeekableEnd) ||
				!vSeekableEnd ||
				!mediaElement.played.length
			) {
				return;
			}

			mediaElement.currentTime = vSeekableEnd - 1; //subtracting 1 seems to help avoid undesirable player stop and completion events from being fired

			// saftey check to ensure that the video is actually playing (found this to be an issue in IE)
			if (mediaElement.paused) {
				this.playInternal();
			}
		}
	}

	private triggerChangeDetection(): void {
		this.zone.run(NOOP);
	}

	public readonly handleUnmutedAutoplayError = _once(() => {
		this.resetVolumeIfNeeded();

		this.play();
	});
}
